import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alerte, Bouton, Champ, ChampJours } from '../ui';
import { messageDErreur, useSession } from '../auth/session';
import { periodesDemandees } from './periodes';
import type { ParentDuDossier } from './types';

/**
 * Demande d'échange de jours.
 *
 * Rien n'est modifié à l'envoi : la demande attend l'accord de l'autre parent. L'écran le
 * dit explicitement, pour qu'aucun des deux ne croie le calendrier changé alors qu'il ne
 * l'est pas — un malentendu de ce type se paie en confiance.
 */
export function DemandeEchange({
  caseFileId,
  parents,
}: {
  caseFileId: string;
  parents: ParentDuDossier[];
}) {
  const { client } = useSession();
  const queryClient = useQueryClient();
  const [jours, setJours] = useState<string[]>([]);
  const [motif, setMotif] = useState('');
  const [envoyee, setEnvoyee] = useState(false);

  const autre = parents.find((p) => !p.isViewer);
  const moi = parents.find((p) => p.isViewer);

  const demander = useMutation({
    mutationFn: () =>
      client.request(`/case-files/${caseFileId}/proposals`, {
        method: 'POST',
        body: {
          type: 'day_swap',
          payload: { periods: periodesDemandees(jours, moi?.slot ?? 1) },
          ...(motif ? { reason: motif } : {}),
        },
      }),
    onSuccess: () => {
      setEnvoyee(true);
      setJours([]);
      setMotif('');
      void queryClient.invalidateQueries({ queryKey: ['proposals', caseFileId] });
    },
  });

  if (!autre) {
    return (
      <p className="discret">
        Un échange de jours suppose un accord : il sera possible lorsque l'autre parent
        aura rejoint le dossier.
      </p>
    );
  }

  return (
    <>
      <p style={{ marginTop: 0 }}>
        Vous proposez que l'enfant soit chez vous sur les jours choisis. Le calendrier ne
        changera qu'une fois {autre.displayName} d'accord.
      </p>

      {envoyee ? <Alerte titre="Demande envoyée">Elle attend une réponse.</Alerte> : null}
      {demander.isError ? (
        <Alerte titre="La demande n’a pas pu être envoyée">{messageDErreur(demander.error)}</Alerte>
      ) : null}

      {/* Un échange se demande pour les semaines qui viennent : la fenêtre s'ouvre d'un an
          de part et d'autre, et non sur vingt-cinq ans en arrière. */}
      <ChampJours
        label="Jours à échanger"
        valeur={jours}
        onChange={setJours}
        fenetre={{ avant: 1, apres: 1 }}
        aide="Touchez les jours voulus. Ils n'ont pas à se suivre : deux jours séparés font deux périodes."
      />
      <Champ
        label="Motif (facultatif)"
        value={motif}
        onChange={(e) => setMotif(e.target.value)}
        aide="Facultatif. Une raison factuelle suffit."
      />

      <Bouton onClick={() => demander.mutate()} disabled={jours.length === 0 || demander.isPending}>
        {demander.isPending ? 'Envoi en cours…' : 'Envoyer la demande'}
      </Bouton>
    </>
  );
}
